'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { AlertCircle, ExternalLink, Eye, Heart, Plus, Ruler } from 'lucide-react';
import { StandardLayout, Button, StarRating } from '@/app/components';
// These are now handled within their respective component directories or need correct paths if kept here
import '@/app/components/layout/Hero.scss';
import '@/app/components/ui/Card.scss';
import '@/app/components/ui/Loading.scss';

import './Home.scss';
import { useSite } from '@/lib/site-context';
import { isTauri } from '@/lib/runtime-bridge';
import { desktopService, LocalSketch } from '@/lib/desktop-service';
import { FolderOpen, Settings } from 'lucide-react';


function BetaWarning() {
  const site = useSite();

  const tiles = [
    {
      title: "Report a Bug",
      icon: "🐛",
      href: "/bug-report",
      desc: (
        <>
          <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>{site.displayName} is in beta</span>
          <br />
          Things may break, features may change, data may poof. Help me squash bugs!
        </>
      ),
      cta: "Report Issue",
      external: false
    },
    ...(site.id === 'py' ? [
      {
        title: "Py2G Desktop",
        icon: "🖥️",
        href: "#",
        desc: "Run PY2G locally on your machine with native Python performance and package management.",
        cta: <em>Available Soon</em>,
        external: false
      }
    ] : [
      {
        title: "Py2G",
        icon: "🐍",
        href: "https://py2g.com",
        desc: "Prefer Python? Switch to the original Python-based version of the platform.",
        cta: "Go to Py2G",
        external: true
      }
    ]),
    {
      title: "VolcoGUI",
      icon: "🧊",
      href: "https://github.com/kylegrover/volcogui",
      desc: "Visualize your G-code with advanced voxel simulation to preview the print process.",
      cta: "Get VolcoGUI",
      external: true
    },
    ...(site.id === 'py' ? [
      {
        title: "JS2G",
        icon: "⚡",
        href: "https://js2g.com",
        desc: "Prefer JavaScript? Try the JS-based flavor running entirely in the browser.",
        cta: "Go to JS2G",
        external: true
      }
    ] : [
      {
        title: "FullControl JS",
        icon: "📦",
        href: "https://www.npmjs.com/package/fullcontrol-js",
        desc: "The underlying library powering JS2G. Use it directly in your own projects.",
        cta: "View Package",
        external: true
      }
    ])
  ];

  return (
    <div className="link-tiles-container">
      <div className="link-tiles-header">
      </div>

      <div className="link-tiles-grid">
        {tiles.map((tile, i) => (
          <Link
            key={i}
            href={tile.href}
            target={tile.external ? "_blank" : undefined}
            className="link-tile"
          >
            <span className="tile-icon">{tile.icon}</span>
            <span className="tile-title">{tile.title}</span>
            <span className="tile-desc">{tile.desc}</span>
            <span className="tile-cta">{tile.cta}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}


interface Sketch {
  id: string;
  title: string;
  description?: string;
  author: {
    id: string;
    name?: string;
    username?: string;
  };
  createdAt: string;
  views: number;
  likes: number;
  tags: string[];
  svgThumbnail?: string | null;
}

interface Example {
  id: string;
  title: string;
  description?: string;
  filename: string;
  tags: string[];
}

export default function HomePage() {
  const site = useSite();
  const router = useRouter();
  const [sketches, setSketches] = useState<Sketch[]>([]);
  const [examples, setExamples] = useState<Example[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingExamples, setLoadingExamples] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);
  const [sketchesPath, setSketchesPath] = useState<string | null>(null);
  const [localSketches, setLocalSketches] = useState<LocalSketch[]>([]);

  useEffect(() => {
    const tauriDetected = isTauri();
    setIsDesktop(tauriDetected);
    if (tauriDetected) {
      setLoading(false);
      setLoadingExamples(false);
      loadDesktopData();
    } else {
      fetchPublicSketches();
      fetchExamples();
    }
  }, []);

  const loadDesktopData = async () => {
    const path = await desktopService.getSketchesPath();
    setSketchesPath(path);
    if (path) {
      const files = await desktopService.listSketches();
      setLocalSketches(files);
    }
  };

  const handleSelectFolder = async () => {
    const path = await desktopService.selectSketchesFolder();
    if (path) {
      setSketchesPath(path);
      const files = await desktopService.listSketches();
      setLocalSketches(files);
    }
  }; const fetchPublicSketches = async () => {
    try {
      const response = await fetch('/api/sketches/public');
      const data = await response.json();
      setSketches(data.sketches || []);
    } catch (error) {
      console.error('Error fetching sketches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExamples = async () => {
    try {
      const response = await fetch('/api/examples');
      const data = await response.json();
      setExamples(data.examples || []);
    } catch (error) {
      console.error('Error fetching examples:', error);
    } finally {
      setLoadingExamples(false);
    }
  };

  return (
    <StandardLayout>
      <div className="content-wrapper home-page">
        {isDesktop ? (
          <>
            <section className="sketches-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Desktop Mode</h3>
                {sketchesPath && (
                  <Button onClick={handleSelectFolder} type="outline" size="sm" icon={Settings}>
                    Change Folder
                  </Button>
                )}
              </div>

              {!sketchesPath ? (
                <div className="empty-state">
                  <h4>Welcome to py2g Desktop</h4>
                  <p>To get started, please select a folder where your sketches will be saved.</p>
                  <Button onClick={handleSelectFolder} type="primary" icon={FolderOpen}>
                    Select Sketches Folder
                  </Button>
                </div>
              ) : (
                <>
                  <div className="empty-state" style={{ marginBottom: '2em', padding: '15px' }}>
                    <p style={{ margin: 0 }}>
                      <strong>Sketches Folder:</strong> {sketchesPath}
                    </p>
                  </div>

                  {localSketches.length > 0 ? (
                    <div className="sketch-grid">
                      {localSketches.map((sketch) => (
                        <div
                          key={sketch.id}
                          className="sketch-card"
                          onClick={() => router.push(`/id/${sketch.id}`)}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="sketch-preview">
                            {sketch.svgThumbnail ? (
                              <div className="sketch-thumbnail">
                                <div
                                  className="thumbnail-svg"
                                  dangerouslySetInnerHTML={{ __html: sketch.svgThumbnail }}
                                />
                              </div>
                            ) : (
                              <div className="placeholder-preview">
                                <Ruler size={32} />
                              </div>
                            )}
                          </div>
                          <div className="sketch-info">
                            <h4 className="sketch-title">{sketch.title}</h4>
                            <p className="sketch-author">Local File</p>
                            <p className="sketch-date" style={{ fontSize: '0.8em', color: '#666' }}>
                              {new Date(sketch.updatedAt).toLocaleDateString()}
                            </p>
                            <div className="sketch-card-actions">
                              <Button
                                href={`/id/${sketch.id}`}
                                type="outline"
                                size="sm"
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              >
                                Open
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <h4>No sketches found</h4>
                      <p>Create your first sketch in this folder!</p>
                      <Button href="/create" type="primary" icon={Plus}>
                        New Sketch
                      </Button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        ) : (
          <>
            {/* Hero Section */}
            <section className="hero">
              <h2>
                {site.id === 'py' ? 'Create & Share\nPython 2 G-Code' : 'Create & Share\nJavaScript 2 G-Code'}
              </h2>
              <p>
                {site.id === 'py' ? (
                  <>Design parametric 3D printing toolpaths with Python using the <Link target='_blank' href="https://github.com/fullcontrolxyz/fullcontrol">FullControl library <ExternalLink size="1em" /></Link>. Share your creations with the community and explore designs by others.<br />
                    Also available in JavaScript flavor: <Link target='_blank' href="https://js2g.com">JS2G</Link></>
                ) : (
                  <>Design parametric 3D printing toolpaths with JavaScript using <Link target='_blank' href="https://www.npmjs.com/package/fullcontrol-js">FullControl JS <ExternalLink size="1em" /></Link>. Share your creations with the community and explore designs by others.<br />
                    Also available in Python flavor: <Link target='_blank' href="https://py2g.com">PY2G</Link></>
                )}
              </p>
              <div className="hero-actions">
                <Button href="/create" type="primary" size="lg" icon={Plus}>
                  Start Creating
                </Button>
                <Button href="#sketches" type="outline" size="lg" icon={Eye}>
                  Explore Sketches
                </Button>
              </div>
            </section>
            <BetaWarning />
            {/* Recent Sketches */}
            <section id="sketches" className="sketches-section">
              <h3>Recent Public Sketches</h3>

              {loading ? (
                <div className="loading-sketches">
                  <div className="sketch-grid">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="sketch-card skeleton">
                        <div className="sketch-preview skeleton-box"></div>
                        <div className="sketch-info">
                          <div className="skeleton-line"></div>
                          <div className="skeleton-line short"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : sketches.length > 0 ? (
                <div className="sketch-grid">
                  {sketches.map((sketch) => (
                    <div
                      key={sketch.id}
                      className="sketch-card"
                      onClick={() => router.push(`/id/${sketch.id}/view`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          router.push(`/id/${sketch.id}/view`);
                        }
                      }}
                    >
                      <div className="sketch-preview">
                        {sketch.svgThumbnail ? (
                          <div className="sketch-thumbnail">
                            <div
                              className="thumbnail-svg"
                              dangerouslySetInnerHTML={{ __html: sketch.svgThumbnail }}
                            />
                          </div>
                        ) : (
                          <div className="placeholder-preview">
                            <Ruler size={32} />
                          </div>
                        )}
                      </div>
                      <div className="sketch-info">
                        <h4 className="sketch-title">{sketch.title}</h4>
                        <p className="sketch-author">
                          by {sketch.author.username || 'Anonymous'}
                        </p>
                        <StarRating sketchId={sketch.id} inline showCount />
                        {Array.isArray(sketch.tags) && sketch.tags.length > 0 && (
                          <div className="sketch-tags">
                            {sketch.tags.slice(0, 3).map((tag, i) => (
                              <span key={i} className="tag">{tag}</span>
                            ))}
                          </div>
                        )}
                        <div className="sketch-card-actions">
                          <Button
                            href={`/id/${sketch.id}`}
                            type="outline"
                            size="sm"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            View Code
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <h4>No public sketches yet</h4>
                  <p>Be the first to create and share a sketch!</p>
                  <Button href="/create" type="primary" icon={Plus}>
                    Create First Sketch
                  </Button>
                </div>)}
            </section>

            {/* Examples & Templates */}
            <section className="sketches-section">
              <h3>Examples & Templates</h3>

              {loadingExamples ? (
                <div className="loading-sketches">
                  <div className="sketch-grid">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="sketch-card skeleton">
                        <div className="sketch-preview skeleton-box"></div>
                        <div className="sketch-info">
                          <div className="skeleton-line"></div>
                          <div className="skeleton-line short"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : examples.length > 0 ? (
                <div className="sketch-grid">
                  {examples.map((example) => (
                    <Link key={example.id} href={`/example/${example.id}`} className="sketch-card">
                      <div className="sketch-preview">
                        <div className="placeholder-preview">
                          <Ruler size={32} />
                        </div>
                      </div>
                      <div className="sketch-info">
                        <h4 className="sketch-title">{example.title}</h4>
                        <p className="sketch-author">Example</p>
                        {example.description && (
                          <p className="sketch-description">{example.description}</p>
                        )}
                        {Array.isArray(example.tags) && example.tags.length > 0 && (
                          <div className="sketch-tags">
                            {example.tags.slice(0, 3).map((tag, i) => (
                              <span key={i} className="tag">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <h4>No examples available</h4>
                  <p>Examples will be loaded from the repository.</p>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </StandardLayout>
  );
}
